import React, { useRef, useState } from 'react';
import { IoSend, IoImageOutline, IoCloseCircle, IoHappyOutline, IoMicOutline } from 'react-icons/io5';
import EmojiPicker, { EmojiClickData, Theme } from 'emoji-picker-react';
import { Message } from '../../types';

interface ChatInputProps {
  inputMessage: string;
  selectedImage: File | null;
  imagePreview: string | null;
  uploading: boolean;
  compressing: boolean;
  onMessageChange: (message: string) => void;
  onSendMessage: () => void;
  onImageSelect: (file: File) => void;
  onCancelImage: () => void;
  onImageUpload: () => void;
  // Voice recording props
  isRecording: boolean;
  recordingTime: number;
  audioBlob: Blob | null;
  audioUrl: string | null;
  onStartRecording: () => void;
  onStopRecording: () => void;
  onCancelRecording: () => void;
  onUploadVoice: () => void;
  formatTime: (seconds: number) => string;
  // Reply props
  replyingTo?: Message | null;
  onCancelReply?: () => void;
  otherUserName?: string;
  currentUserId?: string;
}

const ChatInput: React.FC<ChatInputProps> = ({
  inputMessage,
  selectedImage,
  imagePreview,
  uploading,
  compressing,
  onMessageChange,
  onSendMessage,
  onImageSelect,
  onCancelImage,
  onImageUpload,
  isRecording,
  recordingTime,
  audioBlob,
  audioUrl,
  onStartRecording,
  onStopRecording,
  onCancelRecording,
  onUploadVoice,
  formatTime,
  replyingTo,
  onCancelReply,
  otherUserName = 'User',
  currentUserId
}) => {
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file && file.type.startsWith('image/')) {
      onImageSelect(file);
    }
  };

  const onEmojiClick = (emojiData: EmojiClickData) => {
    onMessageChange(inputMessage + emojiData.emoji);
    setShowEmojiPicker(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !selectedImage && !replyingTo) {
      onSendMessage();
    } else if (e.key === 'Enter' && !selectedImage && replyingTo) {
      onSendMessage();
    }
  };

  return (
    <div className="bg-[#09090B]/95 border-t border-[#7C3AED]/20 px-2 py-3 flex-shrink-0 backdrop-blur">
      {/* Reply Preview */}
      {replyingTo && (
        <div className="mb-2 px-3 py-2 bg-white/5 rounded-lg border-l-4 border-[#7C3AED] flex items-start justify-between gap-2">
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold text-[#06B6D4] mb-1">
              Replying to {replyingTo && currentUserId && replyingTo.fromUserId === parseInt(currentUserId) ? 'You' : otherUserName}
            </div>
            <div className="text-xs text-[#A1A1AA] truncate">
              {replyingTo.type === 'Image' ? (
                <span className="italic">📷 Image</span>
              ) : replyingTo.type === 'Voice' ? (
                <span className="italic">🎤 Voice message</span>
              ) : (
                replyingTo.content
              )}
            </div>
          </div>
          {onCancelReply && (
            <button
              onClick={onCancelReply}
              className="text-[#A1A1AA] hover:text-white transition-colors flex-shrink-0"
              type="button"
            >
              <IoCloseCircle size={20} />
            </button>
          )}
        </div>
      )}
      
      {compressing && (
        <div className="mb-2 flex items-center gap-2 text-[#06B6D4] text-sm">
          <div className="w-4 h-4 border-2 border-[#06B6D4] border-t-transparent rounded-full animate-spin" />
          <span>Compressing image...</span>
        </div>
      )}
      {uploading && !isRecording && (
        <div className="mb-2 flex items-center gap-2 text-[#06B6D4] text-sm">
          <div className="w-4 h-4 border-2 border-[#06B6D4] border-t-transparent rounded-full animate-spin" />
          <span>Uploading...</span>
        </div>
      )}
      {isRecording && (
        <div className="mb-2 flex items-center gap-3 px-3 py-2 bg-red-600/20 border border-red-500/50 rounded-lg">
          <div className="flex items-center gap-2 flex-1">
            <div className="w-3 h-3 bg-red-500 rounded-full animate-pulse" />
            <span className="text-red-400 text-sm font-medium">Recording...</span>
            <span className="text-red-300 text-sm font-mono">{formatTime(recordingTime)}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={onStopRecording}
              className="px-3 py-1 bg-red-600 hover:bg-red-700 text-white rounded text-sm transition-colors"
            >
              Stop
            </button>
            <button
              onClick={onCancelRecording}
              className="px-3 py-1 bg-white/10 hover:bg-white/15 text-white rounded text-sm transition-colors"
            >
              Cancel
            </button>
          </div>
        </div>
      )}
      {audioUrl && audioBlob && !isRecording && (
        <div className="mb-3 relative">
          <audio src={audioUrl} controls className="w-full" />
          <div className="absolute -top-2 -right-2 flex gap-2">
            <button
              onClick={onUploadVoice}
              disabled={uploading}
              className="px-3 py-1 bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] text-white rounded text-sm transition-colors disabled:opacity-50"
            >
              {uploading ? 'Sending...' : 'Send'}
            </button>
            <button
              onClick={onCancelRecording}
              className="bg-red-500 rounded-full p-1 disabled:opacity-50"
              disabled={uploading}
            >
              <IoCloseCircle size={20} />
            </button>
          </div>
        </div>
      )}
      {imagePreview && (
        <div className="mb-3 relative inline-block">
          <img src={imagePreview} alt="Preview" className="max-h-32 rounded-lg" />
          <button
            onClick={onCancelImage}
            className="absolute -top-2 -right-2 bg-red-500 rounded-full p-1"
            disabled={uploading || compressing}
          >
            <IoCloseCircle size={20} />
          </button>
        </div>
      )}
      {showEmojiPicker && (
        <div className="absolute bottom-20 right-4 z-50">
          <EmojiPicker onEmojiClick={onEmojiClick} theme={Theme.DARK} />
        </div>
      )}
      <div className="flex gap-0.5 items-center">
        <input
          type="file"
          ref={fileInputRef}
          onChange={handleImageSelect}
          accept="image/*"
          className="hidden"
        />
        <button
          onClick={() => fileInputRef.current?.click()}
          className="p-1.5 hover:bg-[#7C3AED]/15 text-[#A1A1AA] hover:text-white rounded-lg transition-all flex-shrink-0"
        >
          <IoImageOutline size={22} />
        </button>
        <button
          onClick={() => setShowEmojiPicker(!showEmojiPicker)}
          className="p-1.5 hover:bg-[#7C3AED]/15 text-[#A1A1AA] hover:text-white rounded-lg transition-all flex-shrink-0"
        >
          <IoHappyOutline size={22} />
        </button>
        <button
          onClick={isRecording ? onStopRecording : onStartRecording}
          disabled={!!selectedImage || uploading || !!audioBlob}
          className={`p-1.5 hover:bg-[#7C3AED]/15 text-[#A1A1AA] hover:text-white rounded-lg transition-all flex-shrink-0 ${
            isRecording ? 'bg-red-600 text-white animate-pulse' : ''
          } disabled:opacity-50 disabled:cursor-not-allowed`}
          title={isRecording ? 'Stop recording' : 'Record voice message'}
        >
          <IoMicOutline size={22} />
        </button>
        <input
          type="text"
          value={inputMessage}
          onChange={(e) => onMessageChange(e.target.value)}
          onKeyPress={handleKeyPress}
          placeholder="Type a message..."
          className="flex-1 min-w-0 px-2 py-1.5 bg-white/5 border border-white/10 text-white placeholder-[#A1A1AA] rounded-lg focus:ring-2 focus:ring-[#7C3AED] focus:border-[#7C3AED]/50 outline-none text-sm"
          disabled={!!selectedImage || isRecording || !!audioBlob}
        />
        <button
          onClick={selectedImage ? onImageUpload : audioBlob ? onUploadVoice : onSendMessage}
          disabled={
            selectedImage ? (uploading || compressing) : 
            audioBlob ? uploading : 
            !inputMessage.trim() || isRecording
          }
          className="p-1.5 bg-gradient-to-r from-[#7C3AED] to-[#3B82F6] hover:from-[#8B5CF6] hover:to-[#06B6D4] text-white rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed flex-shrink-0 shadow-lg shadow-[#3B82F6]/20"
        >
          {compressing ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : uploading ? (
            <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
          ) : (
            <IoSend size={22} />
          )}
        </button>
      </div>
    </div>
  );
};

export default ChatInput;
